import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authApi, socioApi, ApiError } from "@/api/client";
import { keystore } from "@/keystore";
import { hasKey } from "@/keystore/storage";
import { InvalidPasswordError } from "@/keystore/crypto";
import { PasswordInput } from "@/components/PasswordInput";

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const outcome = await authApi.login(username, password);

      // 1) Chave já está neste device → só desbloqueia.
      if (await hasKey()) {
        await keystore.unlock(password);
        navigate("/dashboard");
        return;
      }

      // 2) Conta nunca ativada → onboarding.
      if (!outcome.smartAccount) {
        navigate("/setup");
        return;
      }

      // 3) Tem conta mas o device não tem a chave. Se o backup está no
      // sistema (modo banco), recupera automaticamente com a senha. Senão,
      // manda importar manualmente (modo navegador/auto-custódia).
      const vault = await socioApi.getKeyVault().catch(() => null);
      if (vault) {
        try {
          await keystore.restoreFromVault(vault, password);
          navigate("/dashboard");
          return;
        } catch (e) {
          if (e instanceof InvalidPasswordError) {
            setError("Senha não conseguiu abrir o backup da chave. Confira a senha.");
            return;
          }
          throw e;
        }
      }

      setError("Sua conta existe, mas a chave não está neste aparelho nem no backup do sistema. Importe a chave que você salvou.");
      navigate("/import");
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro inesperado");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-app-bg">
      <div className="app-card max-w-md w-full p-6">
        <h1 className="text-2xl font-bold mb-1">Goyaz</h1>
        <p className="text-sm text-app-muted mb-4">Entre com seu usuário e senha.</p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">E-mail ou usuário</label>
            <input type="text" required value={username} onChange={(e) => setUsername(e.target.value)}
                   className="w-full px-3 py-2" autoComplete="username"
                   placeholder="seu@email.com" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Senha</label>
            <PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)}
                           autoComplete="current-password" />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button type="submit" disabled={loading}
                  className="w-full bg-app-accent text-app-accent-text rounded py-2 font-medium disabled:opacity-50 hover:opacity-90">
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>
        <p className="text-sm mt-4 text-app-muted">
          Primeira vez? <Link className="underline hover:text-app-accent" to="/setup">Ative sua conta</Link>
        </p>
      </div>
    </div>
  );
}
